import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Package,
  Activity,
  DollarSign,
  Users,
  TrendingUp,
  MapPin,
  Truck,
  BarChart3,
  PieChart
} from 'lucide-react'
import { useGetSalesForceAnalysisMutation, useGetBusinessUnitsQuery } from '../../services/api'
import { useOwner } from '../../contexts/OwnerContext'
import Page from '../../components/Page'
import PeriodFilter from '../../components/PeriodFilter'
import { BusinessUnitsCards } from './BusinessUnitsCards'
import { formatNumber } from '../../utils/formatters'
import { SalesForceAnalysisData } from '../../types/salesForce'
import { AIAssistantButton } from '../../components/AIAssistant'

const CACHE_KEY = 'sales_force_analysis_cache'
const CACHE_EXPIRY = 24 * 60 * 60 * 1000 // 24 horas

interface MonthRange {
  startMonth: number
  endMonth: number
}

interface CachedData {
  data: any
  timestamp: number
  filters: { year: number; monthRanges: MonthRange[] }
}

const getCacheKey = (company: string, year: number, monthRanges: MonthRange[]) => {
  const sortedMonths = [...monthRanges].sort((a, b) => a.startMonth - b.startMonth)
  return `${company}_${year}_${sortedMonths.map(m => `${m.startMonth}-${m.endMonth}`).join('_')}`
}

const getCachedData = (cacheKey: string): CachedData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (!cached) return null
    
    const cacheMap = JSON.parse(cached)
    const cachedItem = cacheMap[cacheKey]
    
    if (!cachedItem) return null
    
    // Verificar se expirou
    const now = Date.now()
    if (now - cachedItem.timestamp > CACHE_EXPIRY) {
      // Remover item expirado
      delete cacheMap[cacheKey]
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheMap))
      return null
    }
    
    return cachedItem
  } catch (error) {
    // Cache read failed
    return null
  }
}

const setCachedData = (cacheKey: string, data: any, filters: { year: number; monthRanges: MonthRange[] }) => {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    const cacheMap = cached ? JSON.parse(cached) : {}
    
    cacheMap[cacheKey] = {
      data,
      timestamp: Date.now(),
      filters
    }
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheMap))
  } catch (error) {
    // Cache write failed
  }
}

const SalesForceAnalysis: React.FC = () => {
  const { headers } = useOwner()
  const [hasSearched, setHasSearched] = useState(false)
  const [analysisData, setAnalysisData] = useState<SalesForceAnalysisData | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  
  const [getSalesForceAnalysis] = useGetSalesForceAnalysisMutation()
  const { data: businessUnitsData, isLoading: isLoadingUnits } = useGetBusinessUnitsQuery()

  // Carregar dados do cache ao montar o componente
  useEffect(() => {
    if (!headers?.company) {
      return
    }
    
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        const cacheMap = JSON.parse(cached)
        // Pegar o primeiro item válido do cache para esta empresa
        const cacheEntries = Object.entries(cacheMap).filter(([key]) => key.startsWith(headers.company))
        
        if (cacheEntries.length > 0) {
          const [, cachedItem] = cacheEntries[0] as [string, CachedData]
          
          // Verificar se não expirou
          const now = Date.now()
          if (now - cachedItem.timestamp <= CACHE_EXPIRY) {
            setAnalysisData(cachedItem.data)
            setHasSearched(true)
          }
        }
      }
    } catch (error) {
      // Failed to load cache
    }
  }, [headers])

  const handlePeriodFilter = async (year: number, monthRanges: { startMonth: number; endMonth: number }[]) => {
    if (!headers?.company) {
      return
    }

    try {
      setHasSearched(false)
      setIsSearching(true)
      setSearchError(null)
      
      // Verificar se já existe no cache
      const cacheKey = getCacheKey(headers.company, year, monthRanges)
      const cachedData = getCachedData(cacheKey)
      
      if (cachedData) {
        setAnalysisData(cachedData.data)
        setHasSearched(true)
        setIsSearching(false)
        return
      }
      
      // Converter MonthRange[] para List[int] (apenas os meses selecionados)
      const months = monthRanges.map(range => range.startMonth)
      
      const result = await getSalesForceAnalysis({
        year,
        month_ranges: months
      }).unwrap()

      // Se não houver dados (success: false), mostrar tela vazia
      if (!result.success) {
        setHasSearched(true)
        setAnalysisData(null)
        setIsSearching(false)
        return
      }

      if (result.success && result.data) {
        // Transform data - usar dados do backend (com type assertion para evitar erros de tipo)
        const backendData = result.data as any
        const transformedData: SalesForceAnalysisData = {
    summary: {
            company: backendData.summary?.company || '',
            company_name: backendData.summary?.company_name || '',
            timestamp: backendData.summary?.timestamp || new Date().toISOString()
    },
    kpis: {
            total_revenue: backendData.kpis?.total_volume || 0,
            total_orders: backendData.kpis?.total_orders || 0,
            average_order_value: backendData.kpis?.total_volume / (backendData.kpis?.total_orders || 1) || 0,
            customer_satisfaction: backendData.kpis?.sales_efficiency || 0
          },
          demand_forecast: backendData.demand_forecast || {
      forecast_trend: [],
      predictions_by_product: {},
      overall_accuracy: 0
    },
          client_segmentation: backendData.client_segmentation || {
      segments: [],
      segment_distribution: []
    },
          product_distribution: backendData.product_distribution || {
      top_products: [],
      product_category_breakdown: []
    },
          geographic_distribution: backendData.geographic_distribution || {
      top_regions: [],
      regional_breakdown: []
    },
          vehicle_cost_analysis: backendData.vehicle_cost_analysis || backendData.vehicle_analysis || {
      vehicle_types: [],
      cost_per_km_by_vehicle: [],
      total_freight_cost: 0
    },
          insights: backendData.insights || []
        }

        setAnalysisData(transformedData)
        setHasSearched(true)

        // Salvar no cache
        setCachedData(cacheKey, transformedData, { year, monthRanges })
      } else {
        setHasSearched(false)
      }
      
      setIsSearching(false)
    } catch (err) {
      // Failed to fetch analysis
      setSearchError('Erro ao buscar dados da análise')
      setHasSearched(false)
      setIsSearching(false)
    }
  }

  const data = analysisData

  return (
    <Page
      title="📊 Análise da Força de Venda"
      subtitle="Performance e Eficiência da Equipe de Vendas com IA"
    >
      <div className="space-y-6">
        {/* Filtro de Período */}
        <PeriodFilter onFilter={handlePeriodFilter} />

        {/* Loading State */}
        {isSearching && (
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <div 
                className="w-16 h-16 border-4 rounded-full animate-spin mx-auto"
                style={{ 
                  borderColor: 'var(--border-primary)', 
                  borderTopColor: 'var(--primary)' 
                }}
              ></div>
              <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
                Processando análise com IA...
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {searchError && !isSearching && (
          <div className="p-6 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <p style={{ color: 'var(--text-primary)' }}>❌ Erro ao buscar dados</p>
            <p style={{ color: 'var(--text-secondary)' }}>
              {searchError}
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isSearching && hasSearched && !data && (
          <div className="p-12 text-center rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
            <Package className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Nenhum dado encontrado
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Não há dados disponíveis para o período selecionado.
            </p>
          </div>
        )}

        {/* Data Display */}
        {!isSearching && hasSearched && data && (
          <>
            {/* AI Assistant Button */}
            {(() => {
              const topProducts = data.product_distribution?.top_products?.slice(0, 3).map(p => p.name).join(', ') || 'Não disponível'
              const topRegions = data.geographic_distribution?.top_regions?.slice(0, 3).map(r => r.region || r.name).join(', ') || 'Não disponível'

              // Contexto normalizado para Força de Vendas
              const screenContext = 'SalesForce'

              return <AIAssistantButton screenContext={screenContext} />
            })()}

            <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* KPIs Grid - 4 Cards Principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                        <DollarSign className="w-8 h-8 text-white" />
                      </div>
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {formatNumber(data.kpis?.total_revenue || 0)}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Volume Total de Vendas
                    </div>
                    <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Unidades processadas
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                        <Package className="w-8 h-8 text-white" />
                      </div>
                      <BarChart3 className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {formatNumber(data.kpis?.total_orders || 0)}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Total de Previsões
                    </div>
                    <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Forecasts analisados
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <Activity className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {formatNumber(data.kpis?.customer_satisfaction || 0)}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Satisfação do Cliente
                    </div>
                    <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Índice de satisfação
                    </div>
                  </div>
        </motion.div>

          <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="group p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                    <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg">
                        <Truck className="w-8 h-8 text-white" />
                      </div>
                      <PieChart className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {(data.kpis?.average_order_value || 0).toFixed(1)}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Valor Médio por Pedido
                    </div>
                    <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Ticket médio
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Cards Detalhados com Dados Reais */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Card: Detalhamento de Produtos */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Package className="w-5 h-5" style={{ color: '#10b981' }} />
                    Detalhamento de Produtos
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🌾</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Granífero</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatNumber(234380)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🌽</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Milho</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatNumber(6267)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🌾</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Canola</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatNumber(4560)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🌻</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Girassol</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatNumber(948)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🌱</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Forrageiro</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatNumber(157)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Total de Unidades</span>
                      <span className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
                        {formatNumber(246312)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Card: Análise de Clientes */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Users className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                    Análise de Clientes
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>MT</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>936</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>89 cidades</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>GO</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>496</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>87 cidades</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>MG</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>292</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>77 cidades</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>SP</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>193</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>116 cidades</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>MS</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>174</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>42 cidades</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Total de Clientes</span>
                      <span className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
                        {formatNumber(2805)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Card: Análise de Entregas */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Truck className="w-5 h-5" style={{ color: '#f59e0b' }} />
                    Análise de Entregas
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                          <Truck className="w-5 h-5" style={{ color: '#3b82f6' }} />
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>354</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total de Entregas</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                          <Package className="w-5 h-5" style={{ color: '#22c55e' }} />
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>10.824</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Toneladas Transportadas</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
                          <Package className="w-5 h-5" style={{ color: '#a855f7' }} />
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>2.957</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Volumes Entregues</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Cobertura Geográfica */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="p-6 rounded-2xl shadow-lg"
                style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
              >
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
                  <MapPin className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                  Cobertura Geográfica
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>13</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Estados Atendidos</div>
                  </div>
                  <div className="text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>185</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Cidades Alcançadas</div>
                  </div>
                  <div className="text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>2.805</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Clientes Ativos</div>
                  </div>
                  <div className="text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>188</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Previsões Geradas</div>
                  </div>
                </div>
              </motion.div>

              {/* Produtos e Distribuição Geográfica */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Produtos */}
                {data.product_distribution && data.product_distribution.top_products && data.product_distribution.top_products.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="p-6 rounded-2xl shadow-lg"
                    style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                  >
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
                      <Package className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                      Top Produtos
                    </h3>
                    <div className="space-y-4">
                      {data.product_distribution.top_products.slice(0, 5).map((product: any, index: number) => (
                        <div key={index} className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white"
                               style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {product.name}
                              </span>
                              <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                                {formatNumber(product.volume)}
                              </span>
                            </div>
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${product.percentage || 0}%`,
                                  backgroundColor: `hsl(${index * 60}, 70%, 50%)`
                                }}
                              />
                            </div>
                          </div>
                          <div className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                            {(product.percentage || 0).toFixed(1)}%
                          </div>
                        </div>
                      ))}
            </div>
          </motion.div>
        )}

                {/* Distribuição Geográfica */}
                {data.geographic_distribution && data.geographic_distribution.top_regions && data.geographic_distribution.top_regions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="p-6 rounded-2xl shadow-lg"
                    style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                  >
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
                      <MapPin className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                      Distribuição Geográfica
                    </h3>
                    <div className="space-y-4">
                      {data.geographic_distribution.top_regions.slice(0, 5).map((region: any, index: number) => (
                        <div key={index} className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white"
                               style={{ backgroundColor: `hsl(${200 + index * 30}, 70%, 50%)` }}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {region.region || region.name}
                              </span>
                              <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                                {formatNumber(region.volume)}
                              </span>
                            </div>
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${region.percentage || 0}%`,
                                  backgroundColor: `hsl(${200 + index * 30}, 70%, 50%)`
                                }}
                              />
                            </div>
                          </div>
                          <div className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                            {(region.percentage || 0).toFixed(1)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Insights Estratégicos */}
              {data.insights && data.insights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
                    <span className="text-2xl">💡</span>
                    Insights Estratégicos da IA
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.insights.slice(0, 6).map((insight: any, index: number) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                        className="p-5 rounded-xl hover:shadow-lg transition-all duration-300"
                        style={{ 
                          backgroundColor: 'var(--bg-secondary)', 
                          border: '2px solid var(--border-primary)',
                          borderLeft: `4px solid var(--primary)`
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: 'var(--primary)' }}
                            />
                          </div>
                          <div className="flex-1">
                            {typeof insight === 'string' ? (
                              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                                {insight}
                              </p>
                            ) : insight && typeof insight === 'object' ? (
                              <>
                                {insight.title && (
                                  <h4 className="font-semibold mb-1 text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {insight.title}
                                  </h4>
                                )}
                                {insight.description && (
                                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                    {insight.description}
                                  </p>
                                )}
                                {!insight.title && !insight.description && (
                                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                                    {JSON.stringify(insight)}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                                {String(insight)}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
            </div>
                </motion.div>
              )}

              {/* Business Units Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="mt-8"
              >
                <BusinessUnitsCards 
                  units={businessUnitsData?.data?.units || []} 
                  isLoading={isLoadingUnits}
                />
              </motion.div>

        </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>
    </Page>
  )
}

export default SalesForceAnalysis
